import { z } from 'zod';
import { TELEGRAM_PRESET } from './presets/telegram.js';
import { TIKTOK_PRESET } from './presets/tiktok.js';
import { X_PRESET } from './presets/x.js';
import { YOUTUBE_PRESET } from './presets/youtube.js';
/**
 * A social network described as data.
 *
 * Every network that is not Meta is reached through one engine
 * (apps/api/src/social/engine.ts) that reads this contract: how an account is
 * connected, how a post is written, how media gets there, and how a post is
 * read back, edited and removed. Adding a network is writing one of these —
 * from a preset below, or from scratch on the networks screen — not code.
 *
 * Template strings use `{{name}}` placeholders (see SOCIAL_PLACEHOLDERS). The
 * access token is never written into a template: the engine attaches it as the
 * contract's `tokenPlacement` says.
 */
/** Names a template may use. Dotted names read into objects: `{{account.id}}`. */
export const SOCIAL_PLACEHOLDERS = [
    'text',
    'link',
    'account',
    'postId',
    'mediaId',
    'mediaIds',
    'mediaUrl',
    'apiVersion',
    'setting',
    'count',
    'fileSize',
    'mimeType',
    'uploadToken',
    'partIds',
    'entry',
    'item',
    /** The first line of the text, at most 100 characters: a video title. */
    'title',
    'chunkSize',
    'chunkCount',
    'segmentIndex',
    'rangeStart',
    'rangeEnd',
    /** The id the network returned for the new post, before `postIdTemplate` shapes it. */
    'result',
];
const header = z.record(z.string().min(1).max(100), z.string().max(500));
export const socialRequestSchema = z
    .object({
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE']),
    url: z.string().min(8).max(1000),
    headers: header.optional(),
    /** JSON body template. */
    json: z.unknown().optional(),
    /** Form body template (application/x-www-form-urlencoded). */
    form: z.record(z.string(), z.string()).optional(),
    /**
     * Multipart body: text fields (templates), and the name of the field that
     * carries the file being sent, when there is one.
     */
    multipart: z
        .object({
        fields: z.record(z.string(), z.string()).default({}),
        fileField: z.string().min(1).optional(),
    })
        .strict()
        .optional(),
    /** False to skip the contract's `defaultHeaders` for this request. */
    defaultHeaders: z.boolean().optional(),
})
    .strict();
/** Where a value is found in a response: a JSON path, or a response header. */
const resultSchema = z.union([
    z.object({ from: z.literal('body'), path: z.string().min(1) }).strict(),
    z.object({ from: z.literal('header'), name: z.string().min(1) }).strict(),
]);
const textTransform = z.enum(['none', 'linkedin-little']);
/**
 * Wait until the network has finished with a file or a post. The request is
 * repeated until the value at `statePath` is one of `doneValues` (success) or
 * `failedValues` (refused), or `maxWaitMs` passes.
 */
export const pollSchema = z
    .object({
    request: z.lazy(() => socialRequestSchema),
    statePath: z.string().min(1),
    doneValues: z.array(z.string().min(1)).min(1),
    failedValues: z.array(z.string().min(1)).default([]),
    /** An answer with no state at all means done: a network that skips processing for small files. */
    doneWhenMissing: z.boolean().default(false),
    /** When the finished post's id is in the poll answer, where. */
    resultIdPath: z.string().min(1).optional(),
    /** Where a refusal explains itself. */
    errorPath: z.string().min(1).optional(),
    intervalMs: z.number().int().min(500).max(60_000).default(3_000),
    maxWaitMs: z.number().int().min(1_000).max(15 * 60_000).default(5 * 60_000),
})
    .strict();
/**
 * How a file reaches the network before the post refers to it.
 *
 * `upload-url`: one request returns an upload address and the media's id, then
 * the bytes are sent there. `chunked-instructions`: the network answers with
 * the byte ranges to send and where, then a finalize request.
 */
export const mediaStrategySchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('upload-url'),
        init: socialRequestSchema,
        uploadUrlPath: z.string().min(1),
        mediaIdPath: z.string().min(1),
        uploadMethod: z.enum(['PUT', 'POST']).default('PUT'),
        /** Send the access token to the upload address as well. */
        uploadWithAuth: z.boolean().default(true),
    })
        .strict(),
    z
        .object({
        type: z.literal('chunked-instructions'),
        init: socialRequestSchema,
        partsPath: z.string().min(1),
        partUrlField: z.string().min(1),
        firstByteField: z.string().min(1),
        lastByteField: z.string().min(1),
        mediaIdPath: z.string().min(1),
        uploadTokenPath: z.string().optional(),
        /** The finalize request; `{{partIds}}` is the list of ETags collected. */
        finalize: socialRequestSchema,
    })
        .strict(),
    /** The file in one multipart request, answering with the media's id. */
    z
        .object({
        type: z.literal('multipart'),
        request: socialRequestSchema,
        mediaIdPath: z.string().min(1),
        poll: pollSchema.optional(),
    })
        .strict(),
    /**
     * Start, send the file in fixed-size pieces, finish, and optionally wait.
     * A piece goes either as its own request (`append.request`, multipart with
     * `{{segmentIndex}}`) or as a PUT with a byte range to the address the start
     * answered with (`uploadUrlPath`, headers using `{{rangeStart}}` and
     * `{{rangeEnd}}`).
     */
    z
        .object({
        type: z.literal('chunked'),
        init: socialRequestSchema,
        mediaIdPath: z.string().min(1),
        uploadUrlPath: z.string().min(1).optional(),
        chunkSize: z.number().int().min(64 * 1024).max(128 * 1024 * 1024),
        /** The last piece takes the remainder instead of being a short piece of its own. */
        lastChunkAbsorbsRemainder: z.boolean().default(false),
        append: z.discriminatedUnion('mode', [
            z.object({ mode: z.literal('request'), request: socialRequestSchema }).strict(),
            z
                .object({
                mode: z.literal('put'),
                headers: z.record(z.string(), z.string()).default({}),
                withAuth: z.boolean().default(false),
            })
                .strict(),
        ]),
        finalize: socialRequestSchema.optional(),
        poll: pollSchema.optional(),
    })
        .strict(),
    /**
     * A resumable upload: a first request carries the metadata and answers with
     * the address in a header; the file is then sent there whole, and the answer
     * to that names the result.
     */
    z
        .object({
        type: z.literal('resumable'),
        init: socialRequestSchema,
        locationHeader: z.string().min(1).default('location'),
        uploadMethod: z.enum(['PUT', 'POST']).default('PUT'),
        mediaIdPath: z.string().min(1),
        poll: pollSchema.optional(),
    })
        .strict(),
]);
const kindSchema = z.enum(['TEXT', 'LINK', 'PHOTO', 'MULTI_PHOTO', 'VIDEO']);
export const socialNetworkContractSchema = z
    .object({
    version: z.literal(1),
    /** Every address the engine may call, including upload addresses it is handed. `*.example.com` allowed. */
    allowedHosts: z.array(z.string().min(3).max(200)).min(1).max(20),
    auth: z.discriminatedUnion('type', [
        z
            .object({
            type: z.literal('oauth2'),
            authorizeUrl: z.string().url(),
            tokenUrl: z.string().url(),
            revokeUrl: z.string().url().optional(),
            scopes: z.array(z.string().min(1)).max(40),
            /** Extra scopes asked for when an app setting is on, e.g. `{ "organizations": [...] }`. */
            scopesWhen: z.record(z.string(), z.array(z.string().min(1))).optional(),
            scopeSeparator: z.enum([' ', ',']).default(' '),
            pkce: z.boolean().default(false),
            clientIdParam: z.string().default('client_id'),
            clientSecretIn: z.enum(['body', 'basic']).default('body'),
            extraAuthorizeParams: z.record(z.string(), z.string()).optional(),
            refresh: z.boolean().default(true),
        })
            .strict(),
        /**
         * A token pasted in rather than granted through a consent screen (a
         * Telegram bot). It is entered as the app's secret; connecting then asks
         * for the places to post to, each looked up with `accountLookup`.
         */
        z
            .object({
            type: z.literal('token'),
            clientIdLabel: z.string().min(1).max(100),
            clientSecretLabel: z.string().min(1).max(100),
            accountsLabel: z.string().min(1).max(200),
            accountLookup: z
                .object({
                request: socialRequestSchema,
                idPath: z.string().min(1),
                namePath: z.string().min(1),
                /**
                 * What is stored as the account's address, from `{{entry.id}}` (what
                 * the person typed). Absent: the id the lookup answered with.
                 */
                accountId: z.string().min(1).optional(),
                kind: z.enum(['PERSON', 'ORGANIZATION', 'ACCOUNT']),
            })
                .strict(),
        })
            .strict(),
    ]),
    /** How the token rides along on API calls. */
    tokenPlacement: z
        .discriminatedUnion('in', [
        z.object({ in: z.literal('bearer') }).strict(),
        z.object({ in: z.literal('query'), name: z.string().min(1) }).strict(),
        /** Put into the path, right after the host: `https://host/<prefix><token>/rest-of-path`. */
        z.object({ in: z.literal('path-prefix'), prefix: z.string().max(20) }).strict(),
    ])
        .default({ in: 'bearer' }),
    defaultHeaders: header.optional(),
    /** Values the app form offers, readable as `{{setting.<name>}}`. */
    settings: z
        .array(z
        .object({
        name: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
        label: z.string().min(1).max(200),
        type: z.enum(['boolean', 'string']),
        /** Used while the app leaves the setting empty. */
        default: z.union([z.boolean(), z.string().max(500)]).optional(),
        /** A string setting limited to these values, offered as a choice. */
        options: z.array(z.string().min(1).max(100)).min(1).max(20).optional(),
    })
        .strict())
        .default([]),
    defaultApiVersion: z.string().max(40).optional(),
    identity: z
        .object({
        request: socialRequestSchema,
        idPath: z.string().min(1),
        namePath: z.string().min(1),
        /** The account address built from the id, e.g. `urn:li:person:{{entry.id}}`. */
        accountId: z.string().min(1),
        kind: z.enum(['PERSON', 'ORGANIZATION', 'ACCOUNT']),
    })
        .strict(),
    /** More places to post that the person administers (company pages, channels). */
    extraAccounts: z
        .object({
        whenSetting: z.string().optional(),
        request: socialRequestSchema,
        listPath: z.string().min(1),
        idPath: z.string().min(1),
        accountId: z.string().min(1),
        /** A request per entry for its display name, when the list does not carry one. */
        nameRequest: socialRequestSchema.optional(),
        namePath: z.string().min(1),
        fallbackName: z.string().min(1),
        kind: z.enum(['PERSON', 'ORGANIZATION', 'ACCOUNT']),
    })
        .strict()
        .optional(),
    publish: z
        .object({
        kinds: z.array(kindSchema).min(1),
        request: socialRequestSchema,
        /** Merged into the request's JSON body for each kind. */
        perKind: z.record(kindSchema, z.unknown()).default({}),
        /**
         * A kind's own request instead of the shared one: a network with a
         * different call per kind (Telegram's sendPhoto, sendVideo).
         */
        requests: z
            .record(kindSchema, z.object({ request: socialRequestSchema, resultId: resultSchema.optional() }).strict())
            .default({}),
        /** Kinds where uploading the media is itself the post (a YouTube or TikTok video). */
        mediaIsPost: z.array(kindSchema).default([]),
        resultId: resultSchema,
        /** Shapes the stored post id from `{{result}}` and `{{account.id}}`, e.g. `{{account.id}}:{{result}}`. */
        postIdTemplate: z.string().min(1).optional(),
        maxTextLength: z.number().int().min(1).optional(),
        /** `{{title}}` when the post has no text: a network that refuses an empty title. */
        titleFallback: z.string().min(1).max(100).optional(),
        textTransform: textTransform.default('none'),
        minImages: z.number().int().min(1).default(2),
        maxImages: z.number().int().min(1).default(9),
    })
        .strict(),
    media: z
        .object({
        image: mediaStrategySchema.optional(),
        video: mediaStrategySchema.optional(),
    })
        .strict()
        .default({}),
    posts: z
        .object({
        list: z
            .object({
            request: socialRequestSchema,
            listPath: z.string().min(1),
            idPath: z.string().min(1),
            textPath: z.string().min(1),
            createdPath: z.string().min(1),
            createdFormat: z.enum(['epoch-ms', 'epoch-s', 'iso']),
            onlyKinds: z.array(z.enum(['PERSON', 'ORGANIZATION', 'ACCOUNT'])).optional(),
            whenSetting: z.string().optional(),
        })
            .strict()
            .optional(),
        edit: socialRequestSchema.optional(),
        delete: socialRequestSchema.optional(),
        readTextTransform: textTransform.default('none'),
    })
        .strict()
        .default({}),
    permalink: z.string().min(8).max(500),
    /** Paths tried, in order, for an error message in a failed response. */
    errorMessagePaths: z.array(z.string().min(1)).default(['message', 'error_description']),
})
    .strict();
const LINKEDIN_API = 'https://api.linkedin.com';
const linkedinPostUrl = `${LINKEDIN_API}/rest/posts/{{postId}}`;
/**
 * LinkedIn. Profiles post with "Share on LinkedIn"; company pages need the
 * Community Management API, behind the `organizations` app setting. A profile's
 * own posts cannot be read back (LinkedIn reserves r_member_social for approved
 * partners), so listing applies to company pages only.
 */
const LINKEDIN = {
    key: 'LINKEDIN',
    name: 'LinkedIn',
    verifiedLive: true,
    contract: {
        version: 1,
        allowedHosts: ['api.linkedin.com', 'www.linkedin.com', '*.linkedin.com'],
        auth: {
            type: 'oauth2',
            authorizeUrl: 'https://www.linkedin.com/oauth/v2/authorization',
            tokenUrl: 'https://www.linkedin.com/oauth/v2/accessToken',
            revokeUrl: 'https://www.linkedin.com/oauth/v2/revoke',
            scopes: ['openid', 'profile', 'w_member_social'],
            scopesWhen: {
                organizations: ['r_organization_admin', 'w_organization_social', 'r_organization_social'],
            },
            scopeSeparator: ' ',
            pkce: false,
            clientSecretIn: 'body',
            refresh: true,
        },
        tokenPlacement: { in: 'bearer' },
        defaultHeaders: {
            'LinkedIn-Version': '{{apiVersion}}',
            'X-Restli-Protocol-Version': '2.0.0',
        },
        settings: [
            {
                name: 'organizations',
                label: 'Company pages approved (Community Management API)',
                type: 'boolean',
            },
        ],
        defaultApiVersion: '202603',
        identity: {
            request: { method: 'GET', url: `${LINKEDIN_API}/v2/userinfo`, defaultHeaders: false },
            idPath: 'sub',
            namePath: 'name',
            accountId: 'urn:li:person:{{entry.id}}',
            kind: 'PERSON',
        },
        extraAccounts: {
            whenSetting: 'organizations',
            request: {
                method: 'GET',
                url: `${LINKEDIN_API}/rest/organizationAcls?q=roleAssignee&role=ADMINISTRATOR&state=APPROVED&count=50`,
            },
            listPath: 'elements',
            idPath: 'organization',
            accountId: '{{entry.id}}',
            nameRequest: { method: 'GET', url: `${LINKEDIN_API}/rest/organizations/{{entry.idTail}}` },
            namePath: 'localizedName',
            fallbackName: 'Organization {{entry.idTail}}',
            kind: 'ORGANIZATION',
        },
        publish: {
            kinds: ['TEXT', 'LINK', 'PHOTO', 'MULTI_PHOTO', 'VIDEO'],
            request: {
                method: 'POST',
                url: `${LINKEDIN_API}/rest/posts`,
                json: {
                    author: '{{account.id}}',
                    commentary: '{{text}}',
                    visibility: 'PUBLIC',
                    distribution: {
                        feedDistribution: 'MAIN_FEED',
                        targetEntities: [],
                        thirdPartyDistributionChannels: [],
                    },
                    lifecycleState: 'PUBLISHED',
                    isReshareDisabledByAuthor: false,
                },
            },
            perKind: {
                LINK: { content: { article: { source: '{{link}}', title: '{{link}}' } } },
                PHOTO: { content: { media: { id: '{{mediaId}}' } } },
                MULTI_PHOTO: {
                    content: { multiImage: { images: { $each: 'mediaIds', as: { id: '{{item}}' } } } },
                },
                VIDEO: { content: { media: { id: '{{mediaId}}' } } },
            },
            resultId: { from: 'header', name: 'x-restli-id' },
            textTransform: 'linkedin-little',
            minImages: 2,
            maxImages: 9,
        },
        media: {
            image: {
                type: 'upload-url',
                init: {
                    method: 'POST',
                    url: `${LINKEDIN_API}/rest/images?action=initializeUpload`,
                    json: { initializeUploadRequest: { owner: '{{account.id}}' } },
                },
                uploadUrlPath: 'value.uploadUrl',
                mediaIdPath: 'value.image',
                uploadMethod: 'PUT',
                uploadWithAuth: true,
            },
            video: {
                type: 'chunked-instructions',
                init: {
                    method: 'POST',
                    url: `${LINKEDIN_API}/rest/videos?action=initializeUpload`,
                    json: {
                        initializeUploadRequest: {
                            owner: '{{account.id}}',
                            fileSizeBytes: '{{fileSize}}',
                            uploadCaptions: false,
                            uploadThumbnail: false,
                        },
                    },
                },
                partsPath: 'value.uploadInstructions',
                partUrlField: 'uploadUrl',
                firstByteField: 'firstByte',
                lastByteField: 'lastByte',
                mediaIdPath: 'value.video',
                uploadTokenPath: 'value.uploadToken',
                finalize: {
                    method: 'POST',
                    url: `${LINKEDIN_API}/rest/videos?action=finalizeUpload`,
                    json: {
                        finalizeUploadRequest: {
                            video: '{{mediaId}}',
                            uploadToken: '{{uploadToken}}',
                            uploadedPartIds: '{{partIds}}',
                        },
                    },
                },
            },
        },
        posts: {
            list: {
                request: {
                    method: 'GET',
                    url: `${LINKEDIN_API}/rest/posts?q=author&author={{account.id}}&count={{count}}&sortBy=CREATED`,
                    headers: { 'X-RestLi-Method': 'FINDER' },
                },
                listPath: 'elements',
                idPath: 'id',
                textPath: 'commentary',
                createdPath: 'createdAt',
                createdFormat: 'epoch-ms',
                onlyKinds: ['ORGANIZATION'],
                whenSetting: 'organizations',
            },
            edit: {
                method: 'POST',
                url: linkedinPostUrl,
                headers: { 'X-RestLi-Method': 'PARTIAL_UPDATE' },
                json: { patch: { $set: { commentary: '{{text}}' } } },
            },
            delete: {
                method: 'DELETE',
                url: linkedinPostUrl,
                headers: { 'X-RestLi-Method': 'DELETE' },
            },
            readTextTransform: 'linkedin-little',
        },
        permalink: 'https://www.linkedin.com/feed/update/{{postId}}',
        errorMessagePaths: ['message', 'error_description'],
    },
};
/*
 * One file per network under ./presets, so each can be written and reviewed on
 * its own. A file that is not finished exports null and is simply absent here.
 */
export const SOCIAL_PRESETS = [
    LINKEDIN,
    X_PRESET,
    YOUTUBE_PRESET,
    TIKTOK_PRESET,
    TELEGRAM_PRESET,
].filter((preset) => preset !== null);
export function findSocialPreset(key) {
    return SOCIAL_PRESETS.find((preset) => preset.key === key);
}
//# sourceMappingURL=socialNetwork.js.map